// deno-lint-ignore-file no-unused-vars
import { h, Fragment } from '../jsx-runtime.ts';
import type { TaskWithTags, Tag, TaskTag } from '../types.ts';
import { TasksAPI } from '../api.ts';
import { showToast } from './tmk-toast.tsx';
import { renderTagBadge } from './tmk-tag-badge.tsx';

export function openTagPopup(
  tagsArea: HTMLElement,
  task: TaskWithTags,
  allTags: Tag[],
  onUpdated: (updatedTask: TaskWithTags) => void,
): void {
  const existing = document.querySelector('.tags-popup');
  if (existing) existing.remove();

  const popup = <div className="tags-popup" /> as HTMLElement;
  popup.addEventListener('click', (ev: Event) => ev.stopPropagation());
  popup.addEventListener('touchend', (ev: Event) => ev.stopPropagation());

  const taskTagIds = new Set((task.tags || []).map(t => t.tag_id));

  const prefixGroups = new Map<string, Tag[]>();
  for (const tag of allTags) {
    const slashIdx = tag.name.indexOf('/');
    const prefix = slashIdx > 0 ? tag.name.substring(0, slashIdx) : '__none__';
    if (!prefixGroups.has(prefix)) prefixGroups.set(prefix, []);
    prefixGroups.get(prefix)!.push(tag);
  }

  for (const [prefix, tags] of prefixGroups) {
    if (prefix !== '__none__') {
      popup.appendChild(<div style={{
        fontSize: '11px', fontWeight: '600', color: 'var(--color-muted)',
        padding: '4px 8px', textTransform: 'uppercase',
      }}>{prefix}</div> as HTMLElement);
    }

    for (const tag of tags) {
      const isSelected = taskTagIds.has(tag.id);
      const option = <div className={'tag-option' + (isSelected ? ' selected' : '')} /> as HTMLElement;
      const cb = <input type="checkbox" checked={isSelected ? 'checked' : undefined}
        style={{ accentColor: tag.color || 'var(--color-primary)', pointerEvents: 'none' }}
      /> as HTMLInputElement;
      option.appendChild(cb);
      option.appendChild(renderTagBadge({ tag_id: tag.id, tag_name: tag.name, tag_color: tag.color, tag_icon: tag.icon, attribute_values: null }));

      option.addEventListener('click', async (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        cb.checked = !cb.checked;
        option.classList.toggle('selected');

        let newTagIds: string[];
        if (cb.checked) {
          newTagIds = [...taskTagIds];
          if (prefix !== '__none__') {
            const samePrefixIds = tags.map(t => t.id);
            newTagIds = newTagIds.filter(id => !samePrefixIds.includes(id));
          }
          newTagIds.push(tag.id);
          taskTagIds.add(tag.id);
        } else {
          taskTagIds.delete(tag.id);
          newTagIds = [...taskTagIds];
        }

        try {
          await TasksAPI.update(task.id, { tag_ids: newTagIds.map(id => ({ tag_id: id })) });
          // PUT nie zwraca tagów — dociągamy świeże, żeby wołający (lista, szczegóły) renderowali
          // aktualny stan, a nie stare task.tags (spec/lista-zadan.md → Inline editing → Tagi).
          const fresh = await TasksAPI.getById(task.id);
          if (fresh) task.tags = fresh.tags;
          onUpdated(task);
        } catch (err: unknown) {
          showToast('Błąd zapisu tagów: ' + (err as Error).message, 'error');
        }
      });

      popup.appendChild(option);
    }
  }

  // On mobile: fixed overlay. On desktop: absolute inside tagsArea.
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  if (isMobile) {
    popup.classList.add('tags-popup-mobile');
    document.body.appendChild(popup);
  } else {
    tagsArea.appendChild(popup);
  }

  const closeHandler = (ev: Event) => {
    if (!popup.contains(ev.target as Node) && !tagsArea.contains(ev.target as Node)) {
      popup.remove();
      document.removeEventListener('click', closeHandler);
    }
  };
  setTimeout(() => document.addEventListener('click', closeHandler), 0);
}
